import { useChatContext } from '@copilotkit/react-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// import { MessageCircleQuestion } from 'lucide-react';

export const ToggleButton = () => {
  const { open, setOpen } = useChatContext();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [hasMoved, setHasMoved] = useState<boolean>(false);
  const [pointerStart, setPointerStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [buttonStart, setButtonStart] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  // Keep track of screen size to enable drag only under 640px
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize button position to bottom-right on small screens
  useEffect(() => {
    if (!isSmallScreen) return;
    const node = buttonRef.current;
    if (!node) return;

    // If position already set, ensure it's clamped to the viewport on resize/orientation changes
    const clampToViewport = () => {
      const rect = node.getBoundingClientRect();
      const width = rect.width || 64;
      const height = rect.height || 64;
      const maxLeft = Math.max(0, window.innerWidth - width - 8);
      const maxTop = Math.max(0, window.innerHeight - height - 8);
      setPosition((prev) => {
        if (!prev) {
          return {
            left: maxLeft,
            top: maxTop,
          };
        }
        return {
          left: Math.min(Math.max(prev.left, 8), maxLeft),
          top: Math.min(Math.max(prev.top, 8), maxTop),
        };
      });
    };

    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    window.addEventListener(
      'orientationchange',
      clampToViewport as EventListener
    );
    return () => {
      window.removeEventListener('resize', clampToViewport);
      window.removeEventListener(
        'orientationchange',
        clampToViewport as EventListener
      );
    };
  }, [isSmallScreen]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!isSmallScreen) return;
      const node = buttonRef.current;
      if (!node) return;

      node.setPointerCapture(e.pointerId);
      const rect = node.getBoundingClientRect();
      setPointerStart({ x: e.clientX, y: e.clientY });
      setButtonStart({
        left: position?.left ?? rect.left,
        top: position?.top ?? rect.top,
      });
      setHasMoved(false);
      setIsDragging(true);
    },
    [isSmallScreen, position]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!isSmallScreen || !isDragging || !pointerStart || !buttonStart)
        return;
      const deltaX = e.clientX - pointerStart.x;
      const deltaY = e.clientY - pointerStart.y;
      const nextLeft = buttonStart.left + deltaX;
      const nextTop = buttonStart.top + deltaY;

      const node = buttonRef.current;
      const rect = node?.getBoundingClientRect();
      const width = rect?.width ?? 64;
      const height = rect?.height ?? 64;

      const minLeft = 8;
      const minTop = 8;
      const maxLeft = Math.max(0, window.innerWidth - width - 8);
      const maxTop = Math.max(0, window.innerHeight - height - 8);

      const clampedLeft = Math.min(Math.max(nextLeft, minLeft), maxLeft);
      const clampedTop = Math.min(Math.max(nextTop, minTop), maxTop);

      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        setHasMoved(true);
      }
      setPosition({ left: clampedLeft, top: clampedTop });
    },
    [isSmallScreen, isDragging, pointerStart, buttonStart]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!isSmallScreen) return;
      const node = buttonRef.current;
      if (
        node &&
        node.hasPointerCapture &&
        node.hasPointerCapture(e.pointerId)
      ) {
        node.releasePointerCapture(e.pointerId);
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      // Treat as click if there was no significant movement
      if (!hasMoved) {
        setOpen(!open);
      }
    },
    [isSmallScreen, hasMoved, open, setOpen]
  );

  return (
    <button
      ref={buttonRef}
      draggable={false}
      className={[
        'copilot-btn',
        'flex items-center justify-center rounded-full size-fit lg:p-3 p-2 max-lg:bottom-0! max-m:bottom-4! bg-[#c6beb1] 2xl:bg-primary shadow-[0_0_40px_0_rgba(0,0,0,0.1)] max-xl:w-18 max-lg:w-15 max-lg:h-15 max-xl:h-18 z-30',
        isSmallScreen ? 'fixed' : 'relative cursor-pointer',
        isSmallScreen && open ? 'hidden' : '',
      ].join(' ')}
      style={
        isSmallScreen && position
          ? { left: position.left, top: position.top, touchAction: 'none' }
          : isSmallScreen
            ? {
                left: undefined,
                top: undefined,
                right: 16,
                bottom: 16,
                position: 'fixed' as const,
                touchAction: 'none',
              }
            : undefined
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={!isSmallScreen ? () => setOpen(!open) : undefined}
      aria-label="Toggle assistant"
    >
      <div className="absolute left-1 bottom-1  bg-[#c6beb1] 2xl:bg-primary w-4 h-4 rotate-90 pointer-events-none select-none" />

      <p className="text-2xl text-white">?</p>
      {/* <MessageCircleQuestion className="text-primary lg:size-8 size-6 inline-block" /> */}
    </button>
  );
};
